import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';
import { X, Pause, Play, Check, Search, Info, AlertTriangle, LifeBuoy } from 'lucide-react';
import { coachApi, onCoachEvent } from './coachApi';
import { autonomousApi } from './autonomousApi';
import AutonomousPanel from './AutonomousPanel';
import { api } from '../lib/api';
import HighlightLayer from './HighlightLayer';

// Bubble tint per branding avatar (matches the chosen robot's color).
const AVATAR_HEX = { purple: '#7c3aed', blue: '#2563eb', teal: '#0d9488', green: '#16a34a', orange: '#ea580c', red: '#dc2626' };

// Embedded Coach shell (Gate-3): natural-language intent → server/model RANKS an
// eligible set (never starts or decides), contextual candidate selection, safe
// low-confidence clarification, novice/expert explanation modes, deviation/recovery
// surfacing, and session resume with server-side revalidation. All completion is
// still decided SERVER-SIDE from trustworthy events / state predicates. The model
// may only phrase — never decide completion, permission, approval, or success.
export default function CoachWidget() {
  const location = useLocation();
  const [open, setOpen] = useState(false);
  const [offers, setOffers] = useState([]);
  const [session, setSession] = useState(null); // {session_id}
  const [step, setStep] = useState(null);
  const [status, setStatus] = useState('idle');
  const [msg, setMsg] = useState('');
  const [hidden, setHidden] = useState(false);
  const [coachEnabled, setCoachEnabled] = useState(true); // tenant on/off switch — hard gate
  const [mode, setMode] = useState('novice'); // novice | expert
  const [intentText, setIntentText] = useState('');
  const [intentBusy, setIntentBusy] = useState(false);
  const [candidates, setCandidates] = useState(null); // {candidates,confidence,need_clarification,clarification,source}
  const [explain, setExplain] = useState(null); // {text,source,mode}
  const [recovery, setRecovery] = useState(''); // deviation/recovery banner
  const [avatar, setAvatar] = useState('purple'); // tenant branding avatar
  const [coachName, setCoachName] = useState('Brandy'); // tenant-configurable Coach name
  const [autonomous, setAutonomous] = useState(false); // tenant autonomous opt-in
  const [surface, setSurface] = useState('walkthroughs'); // 'walkthroughs' | 'autonomous'
  const [escOpen, setEscOpen] = useState(false);
  const [escNote, setEscNote] = useState('');
  const [escErr, setEscErr] = useState('');
  const panelRef = useRef(null);
  const launcherRef = useRef(null);
  const avatarSrc = `/coach_avatars/coach_${avatar}.png`;
  const bubbleHex = AVATAR_HEX[avatar] || AVATAR_HEX.purple;

  // Load tenant Coach config (branding avatar + enabled) once.
  useEffect(() => {
    let ok = true;
    coachApi.config()
      .then((r) => {
        if (!ok) return;
        if (r.avatar) setAvatar(r.avatar);
        if (r.name) setCoachName(r.name);
        if (r.enabled === false) { setHidden(true); setCoachEnabled(false); }
        else setCoachEnabled(true);
        setAutonomous(!!r.autonomous);
        // When the tenant has the autonomous Coach enabled, open on the
        // Autonomous surface so a typed question generates a plan (instead of
        // landing on the walkthrough picker).
        if (r.autonomous) setSurface('autonomous');
      })
      .catch(() => {});
    return () => { ok = false; };
  }, []);

  // Forward route changes to an ACTIVE autonomous plan session (independent of
  // the walkthrough session). The widget is always mounted, so this fires even
  // when the panel is closed and the user is navigating the app.
  useEffect(() => {
    if (autonomousApi.getActive()) {
      autonomousApi.forwardEvent('route_entered', null, { route: location.pathname }).catch(() => {});
    }
  }, [location.pathname]);

  // Offer applicable walkthroughs for the current route (Coach stays silent otherwise).
  useEffect(() => {
    let ok = true;
    coachApi.listWalkthroughs(location.pathname)
      .then((r) => { if (ok) { setOffers(r.walkthroughs || []); setCoachEnabled((en) => { if (en) setHidden(false); return en; }); } })
      .catch((e) => {
        if (!ok) return;
        setOffers([]);
        const m = String(e.message || '');
        if (m.includes('not enabled') || m.includes('403')) setHidden(true);
      });
    return () => { ok = false; };
  }, [location.pathname]);

  // Trusted STATE report on route change during an active session — server decides
  // whether it satisfies the current step's state predicate.
  useEffect(() => {
    if (session && status !== 'completed' && status !== 'cancelled') {
      coachApi.event('route_entered', null, { route: location.pathname }).catch(() => {});
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [location.pathname, session]);

  // React to server DECISIONS on each event — surface deviation/recovery/branch.
  useEffect(() => {
    const off = onCoachEvent((d) => {
      if (!d) return;
      if (d.error && d.recovery) setRecovery(d.recovery);
      else if (d.deviation) setRecovery('That step is still pending — follow the highlighted instruction to continue.');
      else if (d.branch) { setRecovery(''); }
      else if (d.advanced || d.completed) setRecovery('');
    });
    return off;
  }, []);

  const refresh = useCallback(async (sid) => {
    const r = await coachApi.step(sid);
    setStatus(r.status);
    setStep(r.step);
    if (r.status === 'completed') setMsg('Walkthrough complete — nice work.');
    if (r.status === 'stopped_incompatible') setMsg('This walkthrough was updated — please restart it.');
    if (r.status === 'expired') setMsg('This coaching session expired — please start it again.');
  }, []);

  const start = async (wid) => {
    setMsg(''); setExplain(null); setRecovery(''); setCandidates(null);
    const r = await coachApi.start(wid, mode);
    setSession({ session_id: r.session_id });
    setStep(r.step);
    setStatus(r.status);
    setMode(r.explanation_mode || mode);
    setOpen(true);
  };

  // Gate-3: submit a natural-language goal → ranked candidates / clarification.
  const submitIntent = async (e) => {
    e?.preventDefault?.();
    const text = intentText.trim();
    if (!text) return;
    setIntentBusy(true); setCandidates(null);
    try {
      const r = await coachApi.intent(text, location.pathname);
      setCandidates(r);
    } catch (err) {
      setCandidates({ candidates: [], need_clarification: true, clarification: 'Could not match your request — pick a walkthrough below.' });
    } finally {
      setIntentBusy(false);
    }
  };

  // Guided Handoff: when an autonomous plan keeps deviating, drop it and hand the
  // user to the proven Gate-3 walkthrough for the same goal so they're never stuck.
  const handoffToWalkthrough = useCallback(async (goalText) => {
    setSurface('walkthroughs');
    setIntentText(goalText || '');
    setOpen(true);
    if (goalText) {
      try { const r = await coachApi.intent(goalText, location.pathname); setCandidates(r); }
      catch (err) { setCandidates({ candidates: [], need_clarification: true, clarification: 'Pick a walkthrough below.' }); }
    }
  }, [location.pathname]);

  // Poll the current step after events are emitted elsewhere (keeps the shell in sync).
  useEffect(() => {
    if (!session || status === 'completed' || status === 'cancelled' || status === 'stopped_incompatible' || status === 'expired') return undefined;
    const t = setInterval(() => refresh(session.session_id).catch(() => {}), 1200);
    return () => clearInterval(t);
  }, [session, status, refresh]);

  // A11y: move focus into the panel once, when it opens.
  useEffect(() => {
    if (open && panelRef.current) {
      const focusable = panelRef.current.querySelector('button, input, [tabindex]');
      if (focusable) focusable.focus();
    }
  }, [open]);

  const control = async (action) => {
    if (action === 'resume') {
      // Gate-3: resume revalidates the pinned version / expiry / step server-side.
      const r = await coachApi.resume(session.session_id, mode);
      setStatus(r.status);
      if (!r.ok) { setMsg(r.reason || 'This session can no longer be resumed.'); setStep(null); }
      else { setStep(r.step); setRecovery(`Resumed — ${r.reason}`); }
      return;
    }
    const r = await coachApi.control(session.session_id, action);
    setStatus(r.status);
    if (action === 'cancel') { setSession(null); setStep(null); coachApi.end(); }
  };

  const toggleMode = async () => {
    const next = mode === 'expert' ? 'novice' : 'expert';
    setMode(next);
    setExplain(null);
    if (session && (status === 'active' || status === 'paused')) {
      await coachApi.resume(session.session_id, next).catch(() => {});
    }
  };

  const askExplain = async () => {
    if (!session) return;
    const r = await coachApi.explain(session.session_id).catch(() => null);
    if (r) setExplain(r);
  };

  const closePanel = () => { setOpen(false); if (launcherRef.current) launcherRef.current.focus(); };
  const onKeyDown = (e) => { if (e.key === 'Escape') closePanel(); };

  const sendEscalation = async () => {
    const note = escNote.trim();
    if (!note) return;
    setEscErr('');
    try {
      await api.raisePilotEscalation({ category: 'coach', note, session_id: session?.session_id || null });
      setEscOpen(false); setEscNote(''); setMsg('Thanks — your issue was sent to support.');
    } catch (e) {
      // Surface the backend's specific reason (e.g. PII detected) inline in the form.
      setEscErr(e.message || 'Could not send. Please remove any personal or business data and retry.');
    }
  };

  if (hidden || !coachEnabled) return null;

  const terminal = status === 'completed' || status === 'stopped_incompatible' || status === 'expired';

  return (
    <>
      {session && status === 'active' && step && (
        <HighlightLayer targetId={step.semantic_target_id} />
      )}

      <button
        ref={launcherRef}
        data-testid="coach-launcher"
        onClick={() => setOpen((v) => !v)}
        aria-label={`Open ${coachName}`}
        aria-expanded={open}
        aria-controls="coach-panel"
        style={{ background: `radial-gradient(circle at 50% 32%, ${bubbleHex}66, ${bubbleHex}26)`, boxShadow: `0 8px 22px ${bubbleHex}59`, border: `1px solid ${bubbleHex}80` }}
        className="fixed bottom-24 right-5 z-[70] h-14 w-14 rounded-full backdrop-blur flex items-center justify-center hover:scale-105 transition-transform focus:outline-none focus:ring-2 focus:ring-white/50"
      >
        <img src={avatarSrc} alt="" aria-hidden="true" className="h-12 w-12 object-contain drop-shadow" />
      </button>

      {open && (
        <div
          ref={panelRef}
          id="coach-panel"
          data-testid="coach-panel"
          role="dialog"
          aria-modal="false"
          aria-label={coachName}
          onKeyDown={onKeyDown}
          className="fixed bottom-40 right-5 z-[70] w-80 rounded-2xl border border-border/60 bg-card shadow-2xl p-4"
        >
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <img src={avatarSrc} alt="" aria-hidden="true" className="w-9 h-9 object-contain shrink-0" />
              <div className="leading-tight min-w-0">
                <p className="text-sm font-semibold text-foreground truncate" data-testid="coach-name">{coachName}</p>
                <p className="text-[11px] text-muted-foreground truncate" data-testid="coach-tagline">Let {coachName} show you how</p>
              </div>
            </div>
            <div className="flex items-center gap-2">
              <button
                data-testid="coach-report-issue"
                onClick={() => setEscOpen((v) => !v)}
                title="Report an issue to support"
                aria-label="Report an issue to support"
                aria-pressed={escOpen}
                className="text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 rounded"
              >
                <LifeBuoy className="w-4 h-4" />
              </button>
              <button
                data-testid="coach-mode-toggle"
                onClick={toggleMode}
                aria-pressed={mode === 'expert'}
                title="Switch explanation detail"
                className="text-[10px] px-2 py-0.5 rounded-full border border-border/60 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40"
              >
                {mode === 'expert' ? 'Expert' : 'Novice'}
              </button>
              <button data-testid="coach-close" onClick={closePanel} aria-label={`Close ${coachName}`}
                className="text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 rounded">
                <X className="w-4 h-4" />
              </button>
            </div>
          </div>

          {escOpen && (
            <div className="mb-3 rounded-lg border border-border/50 bg-muted/20 p-2.5 space-y-2" data-testid="coach-escalation-form">
              <p className="text-[11px] text-muted-foreground">Report an issue (no customer data, please)</p>
              <textarea
                data-testid="coach-escalation-note"
                value={escNote}
                onChange={(e) => { setEscNote(e.target.value); if (escErr) setEscErr(''); }}
                rows={2}
                placeholder="What went wrong?"
                className={`w-full text-[13px] px-2 py-1.5 rounded-lg border bg-background focus:outline-none focus:ring-2 resize-none ${escErr ? 'border-red-500/70 focus:ring-red-500/40' : 'border-border/60 focus:ring-primary/40'}`}
              />
              {escErr && (
                <p className="text-[11px] text-red-400 flex items-start gap-1" data-testid="coach-escalation-error" role="alert">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {escErr}
                </p>
              )}
              <div className="flex justify-end gap-2">
                <button onClick={() => { setEscOpen(false); setEscNote(''); setEscErr(''); }} className="text-[11px] text-muted-foreground hover:text-foreground">Cancel</button>
                <button data-testid="coach-escalation-send" onClick={sendEscalation} disabled={!escNote.trim()} className="text-[11px] px-2 py-1 rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-40">Send</button>
              </div>
            </div>
          )}

          {!session && (
            <div className="space-y-3">
              {autonomous && (
                <div className="flex items-center gap-1 rounded-lg bg-muted/40 p-0.5" role="tablist" aria-label="Coach mode">
                  <button data-testid="coach-surface-walkthroughs" role="tab" aria-selected={surface === 'walkthroughs'}
                    onClick={() => setSurface('walkthroughs')}
                    className={`flex-1 text-[11px] px-2 py-1 rounded-md transition-colors ${surface === 'walkthroughs' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                    Walkthroughs
                  </button>
                  <button data-testid="coach-surface-autonomous" role="tab" aria-selected={surface === 'autonomous'}
                    onClick={() => setSurface('autonomous')}
                    className={`flex-1 text-[11px] px-2 py-1 rounded-md transition-colors ${surface === 'autonomous' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:text-foreground'}`}>
                    Autonomous
                  </button>
                </div>
              )}

              {autonomous && surface === 'autonomous' ? (
                <AutonomousPanel onHandoff={handoffToWalkthrough} />
              ) : (
                <>
              {/* Gate-3 natural-language intent entry */}
              <form onSubmit={submitIntent} className="space-y-1.5">
                <label htmlFor="coach-intent-input" className="text-[11px] text-muted-foreground">Tell me what you want to do</label>
                <div className="flex items-center gap-1.5">
                  <input
                    id="coach-intent-input"
                    data-testid="coach-intent-input"
                    value={intentText}
                    onChange={(e) => setIntentText(e.target.value)}
                    placeholder="e.g. add a chart to my report"
                    className="flex-1 text-[13px] px-2.5 py-1.5 rounded-lg border border-border/60 bg-background focus:outline-none focus:ring-2 focus:ring-primary/40"
                  />
                  <button data-testid="coach-intent-submit" type="submit" disabled={intentBusy} aria-label="Find a walkthrough"
                    className="h-8 w-8 flex items-center justify-center rounded-lg bg-primary text-primary-foreground hover:bg-primary/90 disabled:opacity-50 focus:outline-none focus:ring-2 focus:ring-primary/40">
                    <Search className="w-4 h-4" />
                  </button>
                </div>
              </form>

              {candidates && (
                <div data-testid="coach-intent-result" className="space-y-1.5">
                  {candidates.need_clarification && (
                    <p data-testid="coach-clarification" className="text-[11px] text-amber-500 flex items-start gap-1" role="status">
                      <Info className="w-3 h-3 mt-0.5 shrink-0" /> {candidates.clarification || 'Which did you mean?'}
                    </p>
                  )}
                  {(candidates.candidates || []).map((c) => (
                    <button key={c.walkthrough_id} data-testid={`coach-candidate-${c.walkthrough_id}`} onClick={() => start(c.walkthrough_id)}
                      className="w-full text-left text-sm px-3 py-2 rounded-lg border border-border/50 hover:bg-primary/10 hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/40">
                      {c.title}
                    </button>
                  ))}
                </div>
              )}

              {/* Route-contextual offers */}
              {offers.length === 0 ? (
                <p className="text-xs text-muted-foreground" data-testid="coach-no-offers">No walkthroughs for this page yet.</p>
              ) : (
                <div className="space-y-1.5">
                  <p className="text-[11px] text-muted-foreground">Guided walkthroughs for this page:</p>
                  {offers.map((w) => (
                    <button key={w.walkthrough_id} data-testid={`coach-start-${w.walkthrough_id}`} onClick={() => start(w.walkthrough_id)}
                      className="w-full text-left text-sm px-3 py-2 rounded-lg border border-border/50 hover:bg-primary/10 hover:border-primary/40 focus:outline-none focus:ring-2 focus:ring-primary/40">{w.title}</button>
                  ))}
                </div>
              )}
                </>
              )}
            </div>
          )}

          {session && terminal && (
            <div data-testid="coach-complete" className={`text-sm flex items-center gap-2 ${status === 'completed' ? 'text-emerald-500' : 'text-amber-500'}`} role="status">
              {status === 'completed' ? <Check className="w-4 h-4" /> : <AlertTriangle className="w-4 h-4" />} {msg}
            </div>
          )}

          {session && step && !terminal && (
            <div data-testid="coach-step">
              <div className="text-[11px] text-muted-foreground mb-1">
                Step {(step.index ?? 0) + 1} of {step.total}{status === 'paused' ? ' · paused' : ''} · {mode}
              </div>
              <div className="text-sm font-medium mb-1">{step.title}</div>
              <p className="text-[13px] text-muted-foreground" aria-live="polite">{step.instruction_text}</p>

              {recovery && (
                <p data-testid="coach-recovery" className="mt-2 text-[11px] text-amber-500 flex items-start gap-1" role="status">
                  <AlertTriangle className="w-3 h-3 mt-0.5 shrink-0" /> {recovery}
                </p>
              )}

              {explain && (
                <div data-testid="coach-explain" className="mt-2 text-[12px] text-foreground/80 bg-muted/40 rounded-lg p-2">
                  {explain.text}
                  <span className="block mt-1 text-[10px] text-muted-foreground" data-testid="coach-explain-source">
                    {explain.source === 'ai' ? 'AI-phrased' : 'Standard guidance'} · {explain.mode}
                  </span>
                </div>
              )}

              <div className="flex items-center gap-3 mt-3 flex-wrap">
                {status === 'active' ? (
                  <button data-testid="coach-pause" onClick={() => control('pause')}
                    className="text-[11px] inline-flex items-center gap-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 rounded">
                    <Pause className="w-3 h-3" /> Pause
                  </button>
                ) : (
                  <button data-testid="coach-resume" onClick={() => control('resume')}
                    className="text-[11px] inline-flex items-center gap-1 text-primary focus:outline-none focus:ring-2 focus:ring-primary/40 rounded">
                    <Play className="w-3 h-3" /> Resume
                  </button>
                )}
                <button data-testid="coach-explain-btn" onClick={askExplain}
                  className="text-[11px] inline-flex items-center gap-1 text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 rounded">
                  <Info className="w-3 h-3" /> Explain
                </button>
                <button data-testid="coach-cancel" onClick={() => control('cancel')}
                  className="text-[11px] text-muted-foreground hover:text-foreground focus:outline-none focus:ring-2 focus:ring-primary/40 rounded">Cancel</button>
              </div>
            </div>
          )}
        </div>
      )}
    </>
  );
}
